import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Send, Check, X, Gift, Phone, CheckCircle2, BookOpen, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SafetyBanner from "../components/SafetyBanner";
import Stars, { RatingLabel } from "../components/Stars";
import BookCover from "../components/BookCover";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

function BookMini({ book, label }) {
  if (!book) return null;
  return (
    <div className="flex items-center gap-3">
      <Link to={`/libro/${book.id}`} className="w-12 h-16 rounded-md bg-muted overflow-hidden shrink-0">
        <BookCover book={book} />
      </Link>
      <div className="min-w-0">
        <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-medium text-sm truncate">{book.title}</p>
        <p className="text-xs text-muted-foreground truncate">{book.author}</p>
      </div>
    </div>
  );
}

export default function ProposalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/proposals/${id}`);
      setP(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); navigate("/area-personale"); }
  }, [id, navigate]);

  const loadMessages = useCallback(async () => {
    try { const { data } = await api.get(`/proposals/${id}/messages`); setMessages(data); } catch {}
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (p && ["accepted", "completed"].includes(p.status)) {
      loadMessages();
      const t = setInterval(loadMessages, 4000);
      return () => clearInterval(t);
    }
  }, [p, loadMessages]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  if (!p) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-muted-foreground">Caricamento…</div>;

  const isOwner = user.id === p.owner_id;
  const other = isOwner ? p.proposer : p.owner;
  const myConfirmed = isOwner ? p.confirmed_by_owner : p.confirmed_by_proposer;
  const otherConfirmed = isOwner ? p.confirmed_by_proposer : p.confirmed_by_owner;

  const respond = async (action) => {
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${id}/respond`, { action }); setP(data); toast.success(action === "accept" ? "Proposta accettata" : "Proposta rifiutata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${id}/confirm`); setP(data); toast.success("Conferma registrata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    try { await api.post(`/proposals/${id}/messages`, { text: t }); loadMessages(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setText(t); }
  };

  const submitReview = async () => {
    if (!rating) { toast.error("Seleziona una valutazione"); return; }
    setBusy(true);
    try {
      await api.post(`/proposals/${id}/review`, { rating, comment });
      toast.success("Grazie per la tua recensione!");
      setRating(0); setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const statusLabel = { pending: "In attesa di risposta", accepted: "Accettata — accordatevi per la consegna", rejected: "Proposta rifiutata", completed: "Scambio completato" }[p.status];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate("/area-personale")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-2" />Area personale</Button>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h1 className="text-xl sm:text-2xl font-serif font-bold">Trattativa</h1>
          <Badge variant="outline" className={p.status === "completed" ? "bg-secondary/15 text-secondary border-secondary/30" : p.status === "accepted" ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300" : p.status === "rejected" ? "bg-stone-200 text-stone-700 border-stone-300" : "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300"}>{statusLabel}</Badge>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 rounded-xl bg-muted/50 p-4">
          <BookMini book={p.book} label="Libro richiesto" />
          {p.is_gift_request ? (
            <div className="flex items-center gap-2 text-sm text-rose-700 dark:text-rose-300"><Gift className="w-5 h-5" />Richiesto in regalo (nessuno scambio)</div>
          ) : <BookMini book={p.offered_book} label="Offerto in cambio" />}
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          {isOwner ? <>Proposta da <Link to={`/utente/${p.proposer?.id}`} data-testid="other-profile-link" className="text-foreground font-semibold hover:text-primary hover:underline">{p.proposer?.name}</Link> ({p.proposer?.comune})</> : <>Inviata a <Link to={`/utente/${p.owner?.id}`} data-testid="other-profile-link" className="text-foreground font-semibold hover:text-primary hover:underline">{p.owner?.name}</Link> ({p.owner?.comune})</>}
        </div>
        <div className="mt-1.5"><RatingLabel rating={other?.rating} /></div>
        {p.message && <p className="mt-2 text-sm rounded-lg bg-muted p-3 italic">"{p.message}"</p>}

        {/* Azioni proprietario */}
        {isOwner && p.status === "pending" && (
          <div className="mt-5 flex gap-3">
            <Button data-testid="accept-button" onClick={() => respond("accept")} disabled={busy} className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground"><Check className="w-4 h-4 mr-2" />Accetta</Button>
            <Button data-testid="reject-button" onClick={() => respond("reject")} disabled={busy} variant="outline" className="flex-1 text-destructive"><X className="w-4 h-4 mr-2" />Rifiuta</Button>
          </div>
        )}
        {!isOwner && p.status === "pending" && (
          <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4" />In attesa che {p.owner?.name} risponda.</div>
        )}
      </div>

      {/* Chat + accordo */}
      {["accepted", "completed"].includes(p.status) && (
        <>
          <SafetyBanner className="mt-6" />

          {other?.phone && (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid="contact-reveal">
              <Phone className="w-4 h-4 text-secondary" />Recapito di <strong>{other.name}</strong>: <span className="font-mono">{other.phone}</span>
            </div>
          )}

          {/* Conferma scambio */}
          {p.status === "accepted" && (
            <div className="mt-4 rounded-2xl border border-border bg-card p-5">
              <p className="font-serif font-semibold mb-1">Conferma dello scambio</p>
              <p className="text-sm text-muted-foreground mb-4">Quando vi siete incontrati e avete scambiato i libri, confermate entrambi. Servono due conferme per chiudere.</p>
              <div className="flex items-center gap-3 flex-wrap">
                <Button data-testid="confirm-button" onClick={confirm} disabled={busy || myConfirmed} className="bg-primary hover:bg-primary/90">
                  <CheckCircle2 className="w-4 h-4 mr-2" />{myConfirmed ? "Hai confermato" : "Conferma scambio avvenuto"}
                </Button>
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  {otherConfirmed ? <><Check className="w-4 h-4 text-secondary" />{other?.name} ha confermato</> : <><Clock className="w-4 h-4" />In attesa di {other?.name}</>}
                </span>
              </div>
            </div>
          )}

          {p.status === "completed" && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-secondary/30 bg-secondary/10 p-5 text-secondary">
              <CheckCircle2 className="w-6 h-6" /><div><p className="font-serif font-semibold text-foreground">Scambio completato!</p><p className="text-sm text-muted-foreground">I libri risultano non più disponibili. Buona lettura!</p></div>
            </div>
          )}

          {p.status === "completed" && (
            <div className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="review-section">
              {p.reviewed_by?.includes(user.id) ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-secondary" />Hai già lasciato una recensione a {other?.name}. Grazie!</div>
              ) : (
                <>
                  <p className="font-serif font-semibold mb-1">Com'è andato lo scambio con {other?.name}?</p>
                  <p className="text-sm text-muted-foreground mb-3">La tua recensione aiuta la comunità a fidarsi.</p>
                  <Stars value={rating} onChange={setRating} size={28} className="mb-3" />
                  <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Un breve commento (facoltativo)…" className="mb-3" />
                  <Button data-testid="review-submit" onClick={submitReview} disabled={busy || !rating} className="bg-primary hover:bg-primary/90">Invia recensione</Button>
                </>
              )}
            </div>
          )}

          {/* Chat */}
          <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-3 border-b font-serif font-semibold">Messaggi</div>
            <div ref={scrollRef} data-testid="chat-messages" className="h-80 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">Nessun messaggio. Scrivi per accordarti su luogo e orario.</p>}
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
                      <p>{m.text}</p>
                      <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={sendMsg} className="flex gap-2 p-3 border-t">
              <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…" className="h-11" />
              <Button data-testid="chat-send" type="submit" size="icon" className="h-11 w-11 bg-primary hover:bg-primary/90 shrink-0"><Send className="w-4 h-4" /></Button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
